import React from 'react';

import { NextPage } from 'next';

import { getServerDataCache } from '@kamernet/core/DataCache/initializeServerDataCache';
import { useIntl } from '@kamernet/core/Intl';
import { PageLayout } from '@kamernet/core/PageLayout';
import { ROUTES } from '@kamernet/core/Routing';

import { definePageLoader } from '@kamernet/modules/definePageLoader';

import {
  FALLBACK_ACTIVE_TENANT_COUNT_NETHERLANDS,
  FALLBACK_AVERAGE_RENTOUT_DAY,
} from '../../constants';

import { CreateAdvert } from './components/CreateAdvert';
import { FAQ } from './components/FAQ';
import { Features } from './components/Features';
import { LandlordReviews } from './components/LandlordReviews';
import { PlaceYourRoom } from './components/PlaceYourRoom';
import { RentingOutOnKamernet } from './components/RentingOutOnKamernet';
import { ThreeSimpleSteps } from './components/ThreeSimpleSteps';
import { TipsAndArticles } from './components/TipsAndArticles';

export interface HomeLandlordProps {
  averageRentoutDayInNL: number;
  activeTenantCountInNL: number;
}

export const getServerSideProps = definePageLoader<HomeLandlordProps>(
  async () => {
    const cityId = null; // We currently do not have city paths, they are still in legacy kamernet

    const serverDataCache = getServerDataCache();

    const activeTenantCount =
      serverDataCache.activeTenantsCount.find(item => item.cityId === cityId)
        ?.total || FALLBACK_ACTIVE_TENANT_COUNT_NETHERLANDS;

    const averageRentoutDays =
      serverDataCache.averageRentoutDays.find(item => item.cityId === cityId)
        ?.numberOfDays || FALLBACK_AVERAGE_RENTOUT_DAY;

    return {
      props: {
        averageRentoutDayInNL: averageRentoutDays,
        activeTenantCountInNL: activeTenantCount,
      },
    };
  },
);

const HomeLandlord: NextPage<HomeLandlordProps> = ({
  averageRentoutDayInNL,
  activeTenantCountInNL,
}) => {
  const { T } = useIntl();

  return (
    <PageLayout
      pageTitle={T('LBL_LANDINGPAGE_SEARCH_TITLE')}
      metaDescription={T(
        'LBL_SUPPLY_HOMEPAGE_META_DESCRIPTION_ROOM_NETHERLANDS',
      )}
      route={ROUTES.RentOutRoom}
    >
      <PageLayout.MainRaw>
        <CreateAdvert
          averageRentoutDayInNL={averageRentoutDayInNL}
          activeTenantCountInNL={activeTenantCountInNL}
        />
        <Features />
        <RentingOutOnKamernet />
        <LandlordReviews />
        <ThreeSimpleSteps />
        <TipsAndArticles />
        <FAQ />
        <PlaceYourRoom />
      </PageLayout.MainRaw>
    </PageLayout>
  );
};

export default HomeLandlord;
